import { Flex, Tag, Tooltip, Typography } from "antd";
import AppPagination from "@lib/components/MiniComponents/Table/AppPagination";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import {
  StyledFlex,
  StyledTableFilterTag,
  StyledTableHeaderContainer,
  StyledTableHeaderInfo,
  StyledTableHeaderItem,
  StyledTableHeaderPagination,
} from "./index.styled";
import TableInfo from "./TableInfo";
import { TableHeaderFilter } from "@lib/types/Api";
import { randomColor } from "@lib/helpers/ColorHelper";
import { gray } from "@ant-design/colors";
import { shortenString } from "@lib/helpers/StringHelper";
import { useIntl } from "react-intl";
import useScreen from "@lib/hooks/useScreen";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { AppTableProps } from "@lib/components/MiniComponents/Table/AppTable";
import AppMobileTableFilters from "./AppMobileTableFilters";

export type AppTableContentHeaderPropsTypes = {
  page: number;
  perPage: number;
  onPageChange?: (page: number, perPage: number) => void;
  query?: UseQueryResult<AxiosResponse<any, any>, Error>;
  components?: JSX.Element;
  showInfo?: true;
  showPagination?: boolean;
  plusProps?: {
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
  };

  filters?: TableHeaderFilter[];
  handleRemoveFilters?: (keys: string[]) => void;
  tableProps?: Partial<AppTableProps<any>>;
};

const AppTableContentHeader = (props: AppTableContentHeaderPropsTypes) => {
  const {
    page,
    perPage,
    onPageChange,
    query,
    components,
    showInfo,
    showPagination = true,
    filters,
    handleRemoveFilters,
    plusProps,
    tableProps,
  } = props;
  const { messages } = useIntl();
  const { isLoading, isFetching } = query || {};
  const loading = isLoading || isFetching;
  const { isMobile } = useScreen();

  const paginationCanShow =
    showPagination && !tableProps?.columnsConfig?.noPagination;
  const filtersCanShow =
    filters && filters.length > 0 && !tableProps?.columnsConfig?.noFilter;
  const infoCanShow = showInfo && query && !tableProps?.columnsConfig?.noInfo;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        marginBottom: isMobile ? "5px" : "0",
      }}
    >
      {filtersCanShow && isMobile && <AppMobileTableFilters {...props} />}

      <StyledTableHeaderContainer>
        {filtersCanShow && !isMobile && (
          <StyledTableHeaderItem className="filters">
            <Typography style={{ marginRight: "10px" }}>
              {messages["common.filters"] as string} :
            </Typography>
            <Tag
              closable
              onClose={() => handleRemoveFilters(filters.map((f) => f.key))}
              color={gray[4]}
            >
              {messages["common.clearAll"] as string}
            </Tag>
            {filters.map((f, index) => {
              return (
                <StyledTableFilterTag
                  closable
                  onClose={() => handleRemoveFilters([f.key])}
                  color={randomColor(index)}
                  key={f.key}
                >
                  {f.title || (messages[`common.${f.key}`] as string)} :{" "}
                  <Tooltip title={f.value}>
                    <small>{shortenString(f.value, 15)}</small>
                  </Tooltip>
                </StyledTableFilterTag>
              );
            })}
          </StyledTableHeaderItem>
        )}
        <StyledTableHeaderItem mobile={isMobile}>
          {infoCanShow && !isMobile && (
            <StyledTableHeaderInfo>
              {!!tableProps?.header?.title ? (
                tableProps?.header?.title
              ) : (
                <TableInfo data={query.data} loading={loading} />
              )}
            </StyledTableHeaderInfo>
          )}

          <StyledTableHeaderPagination mobile={isMobile}>
            <StyledFlex style={{ justifyContent: "flex-end" }}>
              <Flex
                style={{
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                {components}

                {paginationCanShow && (
                  <AppPagination
                    count={query?.data?.data?.total || 0}
                    page={page}
                    perPage={perPage}
                    onChange={onPageChange}
                    showSizeChanger={false}
                  />
                )}

                <Flex
                  style={{
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  {plusProps && (
                    <ActionButton
                      type="add"
                      noLabel
                      noBg
                      size="small"
                      onClick={plusProps?.onClick}
                      disabled={plusProps?.disabled}
                      loading={plusProps?.loading}
                    />
                  )}

                  {query && (
                    <ActionButton
                      type="refresh"
                      noLabel
                      noBg
                      size="small"
                      onClick={() => query?.refetch()}
                      disabled={query?.isFetching}
                      loading={query?.isFetching}
                      query={query}
                    />
                  )}
                </Flex>
              </Flex>
            </StyledFlex>
          </StyledTableHeaderPagination>

          {infoCanShow && isMobile && (
            <StyledTableHeaderInfo>
              {!!tableProps?.header?.title ? (
                tableProps?.header?.title
              ) : (
                <TableInfo data={query.data} loading={loading} />
              )}
            </StyledTableHeaderInfo>
          )}
        </StyledTableHeaderItem>
      </StyledTableHeaderContainer>
    </div>
  );
};

export default AppTableContentHeader;
